import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api, formatApiError } from "../lib/api";
import {
  ArrowLeft, CaretLeft, CaretRight, Crosshair,
  MinusCircle, PlusCircle, X, Check, FlagCheckered,
  Hand, Lightning, ShieldWarning, Flag, WifiSlash, UsersThree, Copy, MapTrifold, Lock, Sparkle, MapPin,
} from "@phosphor-icons/react";
import { useSwingDetector } from "../hooks/useSwingDetector";
import { useWakeLock } from "../hooks/useWakeLock";
import { postScoreWithQueue, subscribe as subscribeQueue } from "../lib/offlineQueue";
import WeatherPill from "../components/WeatherPill";
import ClubSuggestionChip from "../components/ClubSuggestionChip";
import ShotClubPicker from "../components/ShotClubPicker";
import ShotTracker from "../components/ShotTracker";
import SwingCoach from "../components/SwingCoach";
import HoleView from "../components/HoleView";
import RoundMapHero from "../components/RoundMapHero";
import { frontMiddleBack } from "../lib/greenFMB";
import { useAuth } from "../contexts/AuthContext";
import AnimatedNumber from "./round/AnimatedNumber";
import Tile from "./round/Tile";
import GpsRequestButton from "./round/GpsRequestButton";
import PinConfirmStrip from "./round/PinConfirmStrip";
import ScoringStrip from "./round/ScoringStrip";
import TeeSwitchPill from "./round/TeeSwitchPill";
import GpsSignalPill from "./round/GpsSignalPill";
import {
  courseHandicap,
  whsStrokesReceived,
  scoreLabel,
  haversineYards,
  optimisticUpdate,
} from "./round/utils";

export default function Round() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, refresh: refreshUser } = useAuth();
  const isPro =
    user?.role === "admin" ||
    ["active", "trialing"].includes(user?.subscription_status);
  const [round, setRound] = useState(null);
  const [course, setCourse] = useState(null);
  const [holeIdx, setHoleIdx] = useState(0);
  const [direction, setDirection] = useState(1);
  const [distance, setDistance] = useState(null);
  const [distanceSource, setDistanceSource] = useState("");
  const [gpsError, setGpsError] = useState("");
  const [gpsAccuracy, setGpsAccuracy] = useState(null); // metres, lower = better
  const [gpsLastFixAt, setGpsLastFixAt] = useState(null); // ms timestamp
  const [saving, setSaving] = useState(false);
  const [showFinish, setShowFinish] = useState(false);
  const [error, setError] = useState("");
  const [autoSwing, setAutoSwing] = useState(false);
  const [tapMode, setTapMode] = useState(false);
  const [onGreen, setOnGreen] = useState(false);
  const [swingCoachOpen, setSwingCoachOpen] = useState(false);
  const [userPos, setUserPos] = useState(null); // { lat, lng }
  // Breadcrumb trail of GPS fixes for the *current* hole. Resets when the
  // player switches holes. Only points that have moved at least 3yds from the
  // last fix are kept, so the line stays smooth and free of GPS jitter.
  const [breadcrumb, setBreadcrumb] = useState([]); // [{lat, lng}, ...]
  // Stableford / match-play scoring summary from the server. Polled
  // whenever the host's scores change; null for strokeplay rounds.
  const [scoring, setScoring] = useState(null);
  const [transitionDetected, setTransitionDetected] = useState(false);
  const [lastTrigger, setLastTrigger] = useState(null); // { type, at }
  const [activePlayer, setActivePlayer] = useState(0); // 0 = host; >0 = guest (fourball)
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  // Offline sync pip — count of score submissions waiting to be flushed.
  // Populated via subscribeQueue() so the pip updates when strokes are
  // queued OR when the queue drains after the network comes back.
  const [queuedCount, setQueuedCount] = useState(0);
  useEffect(() => {
    return subscribeQueue((n) => setQueuedCount(n));
  }, []);
  const [showInvite, setShowInvite] = useState(false);
  const [showWatchLink, setShowWatchLink] = useState(false);
  const [watchLink, setWatchLink] = useState(null);
  const [watchCopied, setWatchCopied] = useState(false);
  const [wind, setWind] = useState(null); // { mph, relativeDeg, ... }
  // Tee→pin elevation adjustment for the current hole, fetched from
  // /api/courses/{id}/holes/{n}/elevation. Refreshed when the hole changes.
  // Smart Caddie folds this into its wind-adjusted yardage so the
  // recommended club accounts for both wind AND hill.
  const [elevationAdj, setElevationAdj] = useState(null);
  const [clubYardages, setClubYardages] = useState({});
  const [dispersion, setDispersion] = useState({}); // { club_key: {p25,p50,p75,count,...} }
  const [pendingShot, setPendingShot] = useState(null); // { carry, holeNumber }
  const [showHoleView, setShowHoleView] = useState(false);
  const [pinFixMode, setPinFixMode] = useState(false);
  const [autoOnGreenEnabled, setAutoOnGreenEnabled] = useState(true);
  const [autoGreenToast, setAutoGreenToast] = useState(false);
  const manualGreenOverrideRef = useRef(0); // timestamp ms — used to suppress auto-re-enable briefly after a manual OFF
  const shotCandidateRef = useRef(null); // { startDistance, holeNumber, startedAt }
  const watchIdRef = useRef(null);
  // Throttle handle for phone-as-GPS-source broadcasts (last POST timestamp).
  const lastPhonePosPostAtRef = useRef(0);
  const addingRef = useRef(false); // global debounce across input methods

  const shareInvite = async () => {
    if (!round?.join_code) return;
    // Use the API share URL — WhatsApp / iMessage / Twitter crawlers hit this and
    // get a proper preview card. Real humans get auto-forwarded to /join-round/CODE.
    const apiBase = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "") || window.location.origin;
    const url = `${apiBase}/api/share/round/${round.join_code}`;
    const text = `Join my round on Fairway at ${course?.name} — code ${round.join_code}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Join my Fairway round", text, url });
      } else {
        await navigator.clipboard?.writeText(url);
      }
    } catch { /* user cancelled */ }
  };

  // React to network coming/going so the inline banner appears/disappears.
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  // Keep the phone screen awake during the round
  useWakeLock(true);

  // Fetch live scoring summary (Stableford points / match status) whenever
  // the host's score totals change. No-op for strokeplay rounds.
  useEffect(() => {
    const mode = round?.scoring_mode;
    if (!round?.id || !mode || mode === "strokeplay") {
      setScoring(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/rounds/${round.id}/scoring`);
        if (!cancelled) setScoring(data);
      } catch { /* offline / network — silently skip */ }
    })();
    return () => { cancelled = true; };
  }, [round?.id, round?.scoring_mode, round?.total_strokes, round?.players]);

  // Tracks whether we've already snapped the view to the "resume" hole for
  // this round id, so the player only auto-jumps on first load — subsequent
  // setRound calls (e.g. periodic refresh or optimistic updates) leave the
  // chosen hole alone so we don't yank them away mid-scoring.
  const resumeSnappedFor = useRef(null);

  // First-load resume: when a round loads with scored holes, jump straight
  // to the next un-scored hole (capped at the last hole) instead of always
  // landing on hole 1. The player's data is on the server — they just need
  // to land back where they left off.
  useEffect(() => {
    if (!round || !course) return;
    if (resumeSnappedFor.current === round.id) return;
    resumeSnappedFor.current = round.id;

    const allScores = [
      ...(round.scores || []),
      ...((round.players || []).flatMap((p) => p.scores || [])),
    ].filter((s) => s.strokes != null);
    if (allScores.length === 0) return; // brand new round — leave on hole 1

    const lastScoredHole = allScores.reduce(
      (m, s) => Math.max(m, s.hole_number || 0),
      0,
    );
    // Next un-scored hole, but never beyond the last hole on the course.
    const nextHoleNumber = Math.min(course.holes.length, lastScoredHole + 1);
    const idx = course.holes.findIndex((h) => h.number === nextHoleNumber);
    if (idx > 0) setHoleIdx(idx);
  }, [round, course]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // 1) Read cached snapshot first so the screen renders instantly + survives offline.
      try {
        const cachedRound = JSON.parse(localStorage.getItem(`fairway:cached-round:${id}`) || "null");
        if (cachedRound && !cancelled) {
          setRound(cachedRound);
          const cachedCourse = JSON.parse(localStorage.getItem(`fairway:cached-course:${cachedRound.course_id}`) || "null");
          if (cachedCourse) setCourse(cachedCourse);
        }
      } catch (e) {
        // Cached snapshot is best-effort — if it's corrupt we just refetch.
        console.warn("[Round] cached snapshot read failed", e);
      }
      // 2) Refresh from network where possible.
      try {
        const { data: r } = await api.get(`/rounds/${id}`);
        if (cancelled) return;
        setRound(r);
        localStorage.setItem(`fairway:cached-round:${id}`, JSON.stringify(r));
        const { data: c } = await api.get(`/courses/${r.course_id}`);
        if (cancelled) return;
        setCourse(c);
        localStorage.setItem(`fairway:cached-course:${r.course_id}`, JSON.stringify(c));
      } catch (e) {
        // Network failed — only surface an error if we have NO cached fallback to show.
        if (!cancelled) {
          const hadCache = !!localStorage.getItem(`fairway:cached-round:${id}`);
          if (!hadCache) setError(formatApiError(e));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  // While a round is shared (>1 participant), poll every 10s so each player's
  // device shows the latest scores from mates on their own phones.
  useEffect(() => {
    if (!round) return;
    const participants = round.participant_user_ids || [];
    if (participants.length < 2) return;
    let stopped = false;
    const tick = async () => {
      if (stopped) return;
      try {
        const { data } = await api.get(`/rounds/${id}`);
        if (!stopped) {
          setRound(data);
          localStorage.setItem(`fairway:cached-round:${id}`, JSON.stringify(data));
        }
      } catch (e) {
        // Offline / transient network hiccup — keep showing the cached round.
        console.debug("[Round] periodic refresh failed (offline?)", e?.message || e);
      }
    };
    const interval = setInterval(tick, 10000);
    return () => { stopped = true; clearInterval(interval); };
  }, [round, id]);

  const isFourball = Array.isArray(round?.players) && round.players.length > 1;
  const activeScores = isFourball
    ? (round?.players?.[activePlayer]?.scores ?? [])
    : (round?.scores ?? []);

  const currentHole = course?.holes?.[holeIdx];
  const currentScore = activeScores.find((s) => s.hole_number === currentHole?.number);
  const strokes = currentScore?.strokes;
  const putts = currentScore?.putts;

  // If this hole has no pin coords, try OpenStreetMap for a real per-hole
  // green location so live GPS distances become available. Fires once per
  // (course × session); backend has its own 24-hour cooldown so subsequent
  // players who open the same hole don't hammer Overpass.
  const osmTryRef = useRef(new Set());
  useEffect(() => {
    if (!course || !currentHole) return;
    if (currentHole.lat != null && currentHole.lng != null) return;
    if (osmTryRef.current.has(course.id)) return;
    osmTryRef.current.add(course.id);
    (async () => {
      try {
        const { data } = await api.post(`/courses/${course.id}/enrich-osm-pins`);
        if (data?.updated > 0) {
          // Pull the freshly-updated course doc so the live distance kicks in
          const { data: c } = await api.get(`/courses/${course.id}`);
          setCourse(c);
          localStorage.setItem(`fairway:cached-course:${course.id}`, JSON.stringify(c));
        }
      } catch (e) {
        // OSM/Overpass hiccup — user still sees the static "from tee (est)"
        console.debug("[Round] OSM auto-enrich failed", e?.message || e);
      }
    })();
  }, [course, currentHole]);

  // AI hole preview — one-liner Gemini strategy per hole, cached forever on
  // the course doc. Fetched on hole change; falls back silently if API errors.
  const [holePreview, setHolePreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  useEffect(() => {
    if (!course?.id || !currentHole?.number) {
      setHolePreview(null);
      return;
    }
    // If the hole doc already carries a preview (from a previous player's
    // generation), use it immediately — zero network round-trip.
    if (currentHole.ai_preview) {
      setHolePreview(currentHole.ai_preview);
      return;
    }
    setHolePreview(null);
    setPreviewLoading(true);
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/courses/${course.id}/holes/${currentHole.number}/preview`);
        if (!cancelled && data?.preview) setHolePreview(data.preview);
      } catch (e) {
        console.debug("[Round] hole preview fetch failed", e?.message || e);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [course?.id, currentHole?.number, currentHole?.ai_preview]);

  // Pick the yardage to display for this hole, respecting the active player's
  // tee colour (fourball: each player can have their own tee). Falls back to
  // the round-level tee, then the course-default yards.
  const activePlayerTee = (round?.players?.[activePlayer]?.tee_color || "").toLowerCase();
  const teeColor = activePlayerTee || (round?.tee_color || "").toLowerCase();
  const teeAwareYards = (() => {
    if (!currentHole) return null;
    if (teeColor) {
      const match = (currentHole.tees || []).find((t) => (t.color || "").toLowerCase() === teeColor);
      if (match?.yards) return match.yards;
    }
    return currentHole.yards;
  })();

  // GPS distance + position tracking
  useEffect(() => {
    if (!currentHole || !navigator.geolocation) return;
    setGpsError("");
    setDistance(null);
    setTransitionDetected(false);
    setBreadcrumb([]); // fresh trail per hole

    let cancelled = false;
    const onPos = async (pos) => {
      if (cancelled) return;
      const lat = pos.coords.latitude;
      const lng = pos.coords.longitude;
      const acc = pos.coords.accuracy ?? null;
      setUserPos({ lat, lng });
      setGpsAccuracy(acc);
      setGpsLastFixAt(Date.now());
      // Recovery: a fresh fix arriving clears any prior "GPS lost" state so
      // the dot flips back to green automatically when you come out from
      // under tree cover.
      setGpsError(null);
      // Phone-as-GPS-source: broadcast this fix to the backend so the linked
      // watch can read it instead of using its own (typically weaker) GPS
      // antenna. Throttled to one POST every 5s so we don't hammer Mongo —
      // watch polls every 2-3s so 5s broadcast is "always fresh enough".
      const nowMs = Date.now();
      if (nowMs - lastPhonePosPostAtRef.current >= 5000) {
        lastPhonePosPostAtRef.current = nowMs;
        api.post(`/rounds/${round.id}/phone-position`, { lat, lng, accuracy: acc })
          .catch((err) => console.warn("Round: phone-position broadcast failed", err));
      }
      // Append to breadcrumb if moved >=3 yds from the last kept fix.
      // Skip very fuzzy fixes (>25m) so the line stays clean.
      if (acc == null || acc <= 25) {
        setBreadcrumb((prev) => {
          const last = prev[prev.length - 1];
          if (!last) return [{ lat, lng }];
          const moved = haversineYards(last.lat, last.lng, lat, lng);
          if (moved < 3) return prev;
          // Cap to last 400 points per hole to bound memory.
          const next = prev.length >= 400 ? prev.slice(-399) : prev;
          return [...next, { lat, lng }];
        });
      }
      try {
        const { data } = await api.post(
          `/courses/${course.id}/holes/${currentHole.number}/distance${teeColor ? `?tee_color=${encodeURIComponent(teeColor)}` : ""}`,
          { lat, lng }
        );
        if (!cancelled) {
          setDistance(data.distance_yards);
          setDistanceSource(data.source);
        }
      } catch {
        if (!cancelled) {
          setDistance(teeAwareYards);
          setDistanceSource("course_default");
        }
      }
    };
    const onErr = (err) => {
      if (cancelled) return;
      setGpsError(err.message || "Could not get GPS");
      // Resilience: don't blow away a good fix the moment signal dips. The
      // watchPosition handler is STILL running and will recover when signal
      // returns; in the meantime we keep showing the last known distance so
      // the user has a sensible number instead of being flipped back to
      // "385 yds from tee". Only fall back to the course-default tee
      // distance if we *never* had a fix this round (cold-start failure).
      setUserPos((prev) => {
        if (prev) return prev; // keep last known fix → distance stays sane
        // No fix ever → safe to show the static "from-tee" yardage so the
        // hole card isn't blank.
        setDistance(teeAwareYards);
        setDistanceSource("course_default");
        return prev;
      });
    };

    // First fix: explicitly disallow cached positions so we know exactly
    // where the user is *now*, not where they were when they last opened
    // any geolocation-using site.
    navigator.geolocation.getCurrentPosition(onPos, onErr, {
      enableHighAccuracy: true,
      timeout: 8000,
      maximumAge: 0,
    });
    // Live walking poll. CRITICAL: without `maximumAge: 0`, mobile browsers
    // (especially iOS Safari and Android WebView) keep returning the same
    // cached fix indefinitely as you walk — so the distance-to-pin never
    // updates. With `timeout` set, a stalled GPS triggers onErr instead of
    // hanging silently, which lets the UI show "GPS off / re-acquire". The
    // watch stays subscribed across errors and recovers automatically when
    // signal returns (e.g. coming out from under tree cover).
    watchIdRef.current = navigator.geolocation.watchPosition(onPos, onErr, {
      enableHighAccuracy: true,
      maximumAge: 0,
      timeout: 20000,
    });

    return () => {
      cancelled = true;
      if (watchIdRef.current && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, [holeIdx, course?.id, currentHole, teeColor]);

  // Auto-detect transition to next hole + auto-enable on-green
  const nextHole = course?.holes?.[holeIdx + 1];
  useEffect(() => {
    if (!userPos || !currentHole) return;

    const cur = (currentHole.lat != null && currentHole.lng != null)
      ? haversineYards(userPos.lat, userPos.lng, currentHole.lat, currentHole.lng)
      : null;
    const nxt = (nextHole && nextHole.lat != null && nextHole.lng != null)
      ? haversineYards(userPos.lat, userPos.lng, nextHole.lat, nextHole.lng)
      : null;

    // Auto-enable green when within 12 yds of current pin AND not within
    // the 30-second "user just turned it off manually" suppression window.
    const recentlyOverridden =
      Date.now() - (manualGreenOverrideRef.current || 0) < 30_000;
    if (
      autoOnGreenEnabled &&
      cur !== null &&
      cur <= 12 &&
      !onGreen &&
      !recentlyOverridden
    ) {
      setOnGreen(true);
      setAutoGreenToast(true);
      setTimeout(() => setAutoGreenToast(false), 3500);
    }

    // Detect next-hole transition: closer to next pin than current AND within reasonable course bounds
    // Only suggest after the user has logged at least one stroke on this hole.
    if (
      cur !== null && nxt !== null &&
      nxt < cur && nxt < 250 &&
      strokes != null && strokes > 0 &&
      !transitionDetected
    ) {
      setTransitionDetected(true);
    }
  }, [userPos, currentHole, nextHole, strokes, onGreen, transitionDetected, autoOnGreenEnabled]);

  // Tracks the most recent strokes/putts committed locally so rapid taps read
  // the up-to-date value instead of stale React state.
  const localScoreRef = useRef({}); // key: `${playerIdx}-${holeNumber}` -> { strokes, putts }
  const localKey = (p, h) => `${p}-${h}`;

  const saveScore = (next) => {
    if (!currentHole) return;
    setError("");
    const body = {
      hole_number: currentHole.number,
      strokes: next.strokes,
      putts: next.putts ?? null,
      player_index: isFourball ? activePlayer : 0,
      // Include current GPS if we have it — feeds the crowdsourced
      // pin-learning system so under-mapped courses auto-lock a real pin
      // after 3 players score the same hole from similar positions.
      ...(userPos && typeof userPos.lat === "number" && typeof userPos.lng === "number"
        ? { lat: userPos.lat, lng: userPos.lng }
        : {}),
    };
    // 1) Update local committed value immediately so rapid clicks see the new total.
    localScoreRef.current[localKey(body.player_index, body.hole_number)] = {
      strokes: body.strokes,
      putts: body.putts,
    };
    // 2) Optimistic round-state update so the UI re-renders without waiting on the network.
    setRound((r) => optimisticUpdate(r, course, body));
    // 3) Fire-and-forget the network write. postScoreWithQueue handles offline + retries.
    //    When the server responds, reconcile with its authoritative response.
    setSaving(true);
    postScoreWithQueue(id, body)
      .then(({ data, queued }) => {
        if (!queued && data) setRound(data);
      })
      .catch((e) => setError(formatApiError(e)))
      .finally(() => setSaving(false));
  };

  // Read the freshest known strokes/putts for the active hole+player
  // (prefers the local ref, falls back to whatever the server last returned).
  const readCurrent = () => {
    const key = localKey(isFourball ? activePlayer : 0, currentHole?.number);
    const local = localScoreRef.current[key];
    if (local) return local;
    return { strokes, putts };
  };

  // Shot candidate watcher: after the user taps +, wait for the next live GPS
  // distance update. If the distance has materially dropped (>=30 yds, within
  // 380 yds — anything more is GPS noise) we offer to log it as a shot.
  useEffect(() => {
    const cand = shotCandidateRef.current;
    if (!cand) return;
    if (typeof distance !== "number" || distanceSource !== "gps") return;
    if (currentHole?.number !== cand.holeNumber) {
      shotCandidateRef.current = null;
      return;
    }
    // Give GPS a beat to update after the stroke registers.
    if (Date.now() - cand.startedAt < 4000) return;
    const carry = cand.startDistance - distance;
    if (carry >= 30 && carry <= 380) {
      setPendingShot({ carry: Math.round(carry), holeNumber: cand.holeNumber });
    }
    shotCandidateRef.current = null;
  }, [distance, distanceSource, currentHole?.number]);

  // Fallback: if GPS doesn't tick within ~12s of a stroke, prompt anyway so we
  // don't silently drop shots when the player stands still.
  useEffect(() => {
    if (!pendingShot && shotCandidateRef.current) {
      const cand = shotCandidateRef.current;
      const timer = setTimeout(() => {
        const c = shotCandidateRef.current;
        if (!c || c.startedAt !== cand.startedAt) return;
        if (typeof distance === "number" && distanceSource === "gps") {
          const carry = c.startDistance - distance;
          if (carry >= 30 && carry <= 380) {
            setPendingShot({ carry: Math.round(carry), holeNumber: c.holeNumber });
          }
        }
        shotCandidateRef.current = null;
      }, 12000);
      return () => clearTimeout(timer);
    }
  }, [pendingShot, distance, distanceSource]);

  // Reset on-green state when hole changes
  useEffect(() => { setOnGreen(false); }, [holeIdx]);

  // Passive auto-shot detection — if breadcrumb shows a big GPS jump (>50 yds
  // between two consecutive fixes) and we haven't already prompted or logged
  // a shot in the last 25 seconds, ask the player if they just hit one. This
  // is the "Arccos-lite" experience — no sensor, just GPS, no manual tap.
  const lastAutoShotAtRef = useRef(0);
  useEffect(() => {
    if (pendingShot) return; // don't overlap with an existing prompt
    if (shotCandidateRef.current) return; // manual +1 already in flight
    if (breadcrumb.length < 2) return;
    if (typeof distance === "number" && distance < 25) return; // on green — putting
    const now = Date.now();
    if (now - lastAutoShotAtRef.current < 25000) return;

    const a = breadcrumb[breadcrumb.length - 2];
    const b = breadcrumb[breadcrumb.length - 1];
    const jump = haversineYards(a.lat, a.lng, b.lat, b.lng);
    if (jump < 50 || jump > 380) return; // too small = walking, too big = GPS glitch

    lastAutoShotAtRef.current = now;
    setPendingShot({
      carry: Math.round(jump),
      holeNumber: currentHole?.number,
      autoDetected: true,
    });
  }, [breadcrumb, distance, pendingShot, currentHole?.number]);

  // Persistent Android notification during the round — shows current hole,
  // par, distance to pin, and running strokes. Tapping brings the app back
  // to the front. Requires the user to grant Notification permission once.
  // Silent no-op on iOS Safari (doesn't support persistent notifications).
  useEffect(() => {
    if (!course || !currentHole || !round) return;
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (!("serviceWorker" in navigator)) return;
    if (Notification.permission !== "granted") return;

    const payload = {
      body: `Hole ${currentHole.number} · Par ${currentHole.par}${
        typeof distance === "number" ? ` · ${distance} yds to pin` : ""
      }${
        round.total_strokes ? ` · ${round.total_strokes} strokes so far` : ""
      }`,
      icon: "/icon-192.png",
      badge: "/icon-192.png",
      tag: `fairway-round-${round.id}`,   // updates rather than stacks
      renotify: false,
      silent: true,
      requireInteraction: false,
      data: { roundId: round.id, url: `/app/rounds/${round.id}` },
    };
    navigator.serviceWorker.ready.then((reg) => {
      try {
        reg.showNotification("Fairway · Round in progress", payload);
      } catch (e) {
        // Some WebViews throw when showNotification is unavailable — safe to ignore
        console.debug("[round] persistent notif skipped:", e?.message);
      }
    });
  }, [course, currentHole?.number, currentHole?.par, distance, round?.id, round?.total_strokes]);

  // Ask for notification permission ONCE per round on mount, so the persistent
  // banner can appear. Non-blocking — round works fine if user denies.
  useEffect(() => {
    if (!round?.id) return;
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission !== "default") return;   // don't re-prompt
    // Delay a tick so we don't hit them with a prompt before the map renders
    const t = setTimeout(() => {
      try { Notification.requestPermission(); } catch { /* older browsers throw sync */ }
    }, 4000);
    return () => clearTimeout(t);
  }, [round?.id]);

  // Clear the persistent notification when the round page unmounts (user
  // navigated away / finished the round) so a stale card doesn't linger.
  useEffect(() => {
    return () => {
      if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
      navigator.serviceWorker.ready.then((reg) => {
        reg.getNotifications({ tag: `fairway-round-${round?.id}` }).then((list) => {
          list.forEach((n) => n.close());
        }).catch(() => {});
      });
    };
  }, [round?.id]);

  // Fetch tee→pin elevation adjustment whenever we move to a new hole.
  // The endpoint caches for 14 days server-side so this is essentially
  // instant after the first hit per hole.
  useEffect(() => {
    if (!course?.id || !currentHole?.number) {
      setElevationAdj(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/courses/${course.id}/holes/${currentHole.number}/elevation`);
        if (!cancelled) setElevationAdj(data?.elevation_adj || null);
      } catch {
        if (!cancelled) setElevationAdj(null);
      }
    })();
    return () => { cancelled = true; };
  }, [course?.id, currentHole?.number]);

  // Belt-and-braces safety: auto-disable swing detection whenever the player
  // moves to a different hole. Forces the player to re-arm on the new tee so
  // walks/cart rides between holes can never accidentally count as shots.
  useEffect(() => {
    if (autoSwing) setAutoSwing(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holeIdx]);

  // Load the user's club bag + per-club dispersion stats for the chip.
  const refreshDispersion = async () => {
    try {
      const { data } = await api.get("/stats/dispersion");
      const map = {};
      (data?.clubs || []).forEach((c) => {
        map[c.club_key] = c;
      });
      setDispersion(map);
    } catch {
      /* non-fatal */
    }
  };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/me/clubs");
        if (!cancelled) setClubYardages(data?.yardages || {});
      } catch {
        /* non-fatal */
      }
      if (!cancelled) refreshDispersion();
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const updateStrokes = (delta) => {
    if (!currentHole) return;
    const cur = readCurrent();
    let next;
    if (cur.strokes == null) {
      // First tap: each + = exactly 1 stroke (no "auto-jump to par" shortcut).
      // − from an empty hole is a no-op.
      if (delta <= 0) return;
      next = 1;
    } else {
      next = cur.strokes + delta;
    }
    if (next < 1 || next > 15) return;
    // Capture shot candidate ONLY on a manual + when there's a prior stroke,
    // a live GPS distance and we're not already on the green. This lets us
    // measure the carry via the next GPS update.
    if (
      delta > 0 &&
      cur.strokes != null &&
      typeof distance === "number" &&
      distanceSource === "gps" &&
      !onGreen
    ) {
      shotCandidateRef.current = {
        startDistance: distance,
        holeNumber: currentHole.number,
        startedAt: Date.now(),
      };
    }
    saveScore({ strokes: next, putts: cur.putts });
  };

  // +1 stroke AND +1 putt (used when onGreen is active)
  const addStrokeAndPutt = () => {
    if (!currentHole) return;
    const cur = readCurrent();
    let nextStrokes, nextPutts;
    if (cur.strokes == null) {
      // First tap = 1 stroke, 1 putt (no auto-jump to par).
      nextStrokes = 1;
      nextPutts = 1;
    } else {
      nextStrokes = cur.strokes + 1;
      nextPutts = (cur.putts ?? 0) + 1;
    }
    if (nextStrokes > 15 || nextPutts > nextStrokes) return;
    saveScore({ strokes: nextStrokes, putts: nextPutts });
  };

  // Unified +1 stroke. Auto-detection sources (swing/tap-tally) carry a 1.2s cooldown
  // so a single shake/tap can't double-register, but the manual stepper button has no
  // cooldown — rapid manual taps are intentional and each one means a new stroke.
  const addStrokeDebounced = (source) => {
    const isAuto = source === "swing" || source === "tap";
    if (isAuto && addingRef.current) return;
    if (isAuto) {
      addingRef.current = true;
      setTimeout(() => { addingRef.current = false; }, 1200);
    }
    setLastTrigger({ type: source, at: Date.now() });
    if (onGreen) {
      addStrokeAndPutt();
    } else {
      updateStrokes(1);
    }
    // Note: no auto-advance between players. Every hand-off between mates
    // is a MANUAL tap on the "Finish hole · Pass to X →" CTA — that's what
    // the user asked for. Each '+' here logs a shot for the current
    // active player only; the CTA is what changes whose turn it is.
  };

  const swing = useSwingDetector(autoSwing, () => addStrokeDebounced("swing"));

  const onArmSwing = async () => {
    if (autoSwing) {
      setAutoSwing(false);
      return;
    }
    // Warn the user once per session so they understand practice swings count
    if (typeof window !== "undefined" && !window.__fairwaySwingWarned) {
      const ok = window.confirm(
        "Auto-swing detects acceleration spikes from your phone's motion sensor. " +
        "PRACTICE SWINGS, falls, or rough handling can be counted as a real shot.\n\n" +
        "Best for: phone clipped to your golf bag during the actual shot, not warm-ups.\n\n" +
        "Turn on?"
      );
      if (!ok) return;
      window.__fairwaySwingWarned = true;
    }
    if (swing.permission === "prompt") {
      const res = await swing.requestPermission();
      if (res !== "granted") return;
    }
    if (swing.permission === "denied") return;
    setAutoSwing(true);
  };

  const goToHole = (idx) => {
    if (!course) return;
    if (idx < 0 || idx >= course.holes.length) return;
    // Leaving the current hole — if the user logged strokes but no putts, assume the last shot was the holing putt.
    if (idx !== holeIdx && strokes != null && strokes >= 1 && (putts == null || putts === 0)) {
      saveScore({ strokes, putts: 1 });
    }
    setDirection(idx > holeIdx ? 1 : -1);
    setHoleIdx(idx);
  };

  /**
   * Advance to the next thing after a player finishes the current hole.
   * - Solo: jump to the next hole (or do nothing if last hole).
   * - Fourball: hand the phone to the next player who hasn't scored THIS hole
   *   yet. Only when every player has scored do we move to the next hole and
   *   reset the active-player pointer back to the host.
   * Called by every "X-putt → Next hole", "5+ putts" and "Pick up" button so
   * the behaviour stays consistent.
   */
  const advanceAfterScore = () => {
    const currentHoleNum = currentHole?.number;
    const totalHoles = course?.holes?.length ?? 0;

    if (isFourball && round.players && currentHoleNum != null) {
      // Find the next player who has NOT yet scored this hole. Ordering:
      //   1. If we have live GPS positions for the unscored players (via
      //      participant_positions), sort them by DISTANCE-TO-PIN descending
      //      so the golfer furthest from the hole plays next (proper honours
      //      order once everyone's ball is down).
      //   2. Otherwise fall back to sequential (activePlayer + 1) cycling.
      // We read from `localScoreRef` (synchronous) because the just-saved
      // score may not be in the React `round` state yet.
      // A player is "finished the hole" ONLY when they've recorded their
      // putt count (or picked up). Tapping the '+' stroke button DOES NOT
      // finish the hole — it's just one shot for the current player. That
      // way each + cycles to the next unfinished player round-robin, then
      // comes back around for the next set of shots.
      const playerCount = round.players.length;
      const hasFinishedHole = (playerIdx) => {
        const ref = localScoreRef.current[localKey(playerIdx, currentHoleNum)];
        if (ref?.putts != null) return true;
        const p = round.players[playerIdx];
        return (p?.scores || []).some(
          (s) => s.hole_number === currentHoleNum && s.putts != null,
        );
      };
      const unscoredIdx = [];
      for (let step = 1; step <= playerCount; step += 1) {
        const candidateIdx = (activePlayer + step) % playerCount;
        if (candidateIdx === activePlayer) break;
        if (!hasFinishedHole(candidateIdx)) unscoredIdx.push(candidateIdx);
      }
      if (unscoredIdx.length > 0) {
        // Try to sort by furthest-from-pin when we have live GPS for the
        // pin AND at least one unscored player. `haversineMeters` already
        // exists elsewhere in the app — inline the tiny formula here so
        // we don't cross-file couple this hook.
        const pin = currentHole?.lat != null && currentHole?.lng != null
          ? { lat: currentHole.lat, lng: currentHole.lng }
          : null;
        const positions = round?.participant_positions || {};
        const now = Date.now() / 1000;
        const distTo = (playerIdx) => {
          if (!pin) return null;
          const uid = round.players[playerIdx]?.user_id;
          const pos = uid ? positions[uid] : null;
          if (!pos || (now - (pos.ts || 0)) > 60) return null; // stale > 60s
          // Haversine in metres — good enough at hole distances.
          const R = 6371000;
          const toRad = (d) => (d * Math.PI) / 180;
          const dLat = toRad(pin.lat - pos.lat);
          const dLng = toRad(pin.lng - pos.lng);
          const a = Math.sin(dLat / 2) ** 2
            + Math.cos(toRad(pos.lat)) * Math.cos(toRad(pin.lat)) * Math.sin(dLng / 2) ** 2;
          return 2 * R * Math.asin(Math.sqrt(a));
        };
        const dists = unscoredIdx.map((idx) => ({ idx, d: distTo(idx) }));
        const anyKnown = dists.some((x) => x.d != null);
        const nextIdx = anyKnown
          // Furthest-first (nulls sort last).
          ? [...dists].sort((a, b) => {
            if (a.d == null && b.d == null) return 0;
            if (a.d == null) return 1;
            if (b.d == null) return -1;
            return b.d - a.d;
          })[0].idx
          : unscoredIdx[0];
        setActivePlayer(nextIdx);
        return;
      }
      // All players scored this hole in a fourball → DO NOT auto-jump to
      // the next hole. Reset activePlayer back to the host so the "Finish
      // hole · Next →" CTA renders (which needs `strokes > 0` for the
      // active player), and let the user review the hole before manually
      // advancing. This matches the user's expectation that the phone
      // waits for a confirm tap once everyone's finished, rather than
      // silently jumping mid-round.
      setActivePlayer(0);
      return;
    }

    // Solo (non-fourball) flow — the "Finish hole · Next →" CTA already
    // handles the manual advance path; this fallback covers legacy solo
    // rounds that lack a CTA render context.
    if (!isFourball && holeIdx + 1 < totalHoles) {
      goToHole(holeIdx + 1);
    }
  };

  const completeRound = async () => {
    try {
      const { data: completedRound } = await api.post(`/rounds/${id}/complete`);
      // If the round updated the user's WHS Handicap Index, refresh the
      // user in context so the header/profile shows the new value.
      if (completedRound?.handicap_eligible) {
        try { await refreshUser(); } catch { /* non-fatal */ }
      }
      nav(`/app/rounds/${id}/summary`, { replace: true });
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  if (!round || !course) {
    return <div className="px-5 pt-6 text-[var(--text-secondary)]">Loading round...</div>;
  }

  const activePlayerObj = isFourball ? round.players[activePlayer] : null;
  const totalScore = isFourball ? activePlayerObj.total_strokes : round.total_strokes;
  const totalPar = isFourball ? activePlayerObj.total_par : round.total_par;
  const vsPar = totalScore - totalPar;
  const holesPlayed = isFourball ? activePlayerObj.scores.length : round.scores.length;

  return (
    <div className="min-h-screen pb-32">
      {/* Top bar */}
      <div className="glass-header sticky top-0 z-40 px-5 py-3 flex items-center justify-between">
        <button onClick={() => nav("/app")} className="btn-ghost -ml-2" data-testid="round-back-button">
          <ArrowLeft size={20} weight="bold" />
        </button>
        <div className="text-center">
          <div className="label-uppercase">Round at</div>
          <div className="font-heading font-bold text-sm text-[var(--primary)] line-clamp-1 max-w-[180px]">{course.name}</div>
        </div>
        <div className="flex items-center gap-1">
          <GpsRequestButton
            userPos={userPos}
            gpsError={gpsError}
            gpsAccuracy={gpsAccuracy}
            lastFixAt={gpsLastFixAt}
          />
          {round.id && (
            <button
              type="button"
              onClick={async () => {
                try {
                  if (!watchLink) {
                    const { data } = await api.post(`/rounds/${round.id}/watch-link`);
                    setWatchLink(data);
                  }
                  setShowWatchLink(true);
                } catch (e) {
                  /* swallow — non-fatal */
                }
              }}
              data-testid="round-watch-link-button"
              className="btn-ghost"
              aria-label="Get watch link"
              title="Get watch URL (load on a smartwatch browser)"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <rect x="6" y="2" width="12" height="4" rx="1" />
                <rect x="6" y="18" width="12" height="4" rx="1" />
                <rect x="4" y="6" width="16" height="12" rx="3" fill="none" stroke="currentColor" strokeWidth="2" />
                <circle cx="12" cy="12" r="2.5" />
              </svg>
            </button>
          )}
          {round.join_code && (
            <button
              type="button"
              onClick={() => setShowInvite(true)}
              data-testid="round-invite-button"
              className="btn-ghost"
              aria-label="Invite mates"
            >
              <UsersThree size={18} weight="fill" />
            </button>
          )}
          <button onClick={() => setShowFinish(true)} className="btn-ghost" data-testid="round-finish-button">
            <FlagCheckered size={18} weight="fill" /> Finish
          </button>
        </div>
      </div>

      {/* Big OFFLINE banner — visible whenever the device says it's offline.
          Reassures the user that scores are still being saved locally and will sync later. */}
      {!online && (
        <div
          data-testid="round-offline-banner"
          className="mx-5 mt-3 rounded-2xl px-4 py-3 flex items-center gap-3"
          style={{
            background: "linear-gradient(95deg, #C4543C 0%, #D86A52 100%)",
            color: "#F2EEE7",
            boxShadow: "0 8px 24px -6px rgba(196,84,60,0.45)",
          }}
        >
          <div
            className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: "rgba(255,255,255,0.18)" }}
          >
            <WifiSlash size={20} weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-sm leading-tight">You&apos;re offline</div>
            <div className="text-[11px] opacity-90 leading-snug mt-0.5">
              {queuedCount > 0
                ? `${queuedCount} score${queuedCount === 1 ? "" : "s"} saved locally · will auto-sync when you reconnect.`
                : "Every score is saving on your phone — it'll auto-sync when you reconnect."}
            </div>
          </div>
        </div>
      )}

      {/* When back online but there are still queued scores flushing —
          small pip so the user can see "yes it's syncing, don't close the tab yet". */}
      {online && queuedCount > 0 && (
        <div
          data-testid="round-sync-pip"
          className="mx-5 mt-3 rounded-xl px-3 py-2 flex items-center gap-2 text-xs"
          style={{ background: "rgba(20,61,47,0.15)", color: "var(--primary)" }}
        >
          <span
            className="h-2 w-2 rounded-full"
            style={{ background: "var(--primary)", animation: "pulse 1.4s ease-in-out infinite" }}
          />
          <span className="font-bold">
            Syncing {queuedCount} score{queuedCount === 1 ? "" : "s"}…
          </span>
          <span className="opacity-70 italic">back online, catching up</span>
        </div>
      )}

      {/* Mini live leaderboard — sorts by lowest strokes, crown on the leader.
          NOTE: this used to sit at the top of the screen but on long rounds
          you'd have to scroll back up to switch player after entering scores.
          We now render the bar (defined here as `fourballBar` JSX) just above
          the dark-green score-input card so swapping is thumb-reach away. */}

      {/* Active hole card */}
      {course.stats_unknown && (
        <div className="px-5 pt-5 -mb-2">
          <div
            className="rounded-2xl px-4 py-2.5 text-xs flex items-start gap-2"
            data-testid="approx-data-banner"
            style={{
              background: "rgba(251,191,36,0.15)",
              border: "1px solid rgba(251,191,36,0.45)",
              color: "#92400e",
            }}
          >
            <span className="text-base leading-none mt-0.5">⚠</span>
            <div className="flex-1">
              <span className="font-bold uppercase tracking-wider text-[10px]">Approximate course data</span>
              <span className="block text-[11px] opacity-90 mt-0.5">
                Card yardages + pin positions are estimates until verified. GPS is still accurate to your phone. <Link to={`/app/courses/${course.id}`} className="underline font-bold">Submit real stats →</Link>
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Active hole card */}
      <div className="px-5 pt-5 overflow-hidden space-y-3">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={`hero-${currentHole.number}`}
            custom={direction}
            initial={{ opacity: 0, x: 80 * direction }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -80 * direction }}
            transition={{ type: "spring", stiffness: 320, damping: 30 }}
          >
            <RoundMapHero
              holeNumber={currentHole.number}
              par={currentHole.par}
              strokeIndex={currentHole.stroke_index}
              cardYards={teeAwareYards}
              playerPos={userPos}
              matePositions={(() => {
                // Show OTHER participants' live dots on the map (fourball).
                // Filter to fixes newer than 60s + exclude ourselves.
                const pp = round?.participant_positions || {};
                const now = Date.now() / 1000;
                return Object.entries(pp)
                  .filter(([uid, p]) => uid !== user?.id && p && (now - (p.ts || 0)) < 60)
                  .map(([uid, p]) => ({
                    userId: uid,
                    lat: p.lat,
                    lng: p.lng,
                    name: p.name,
                  }));
              })()}
              breadcrumb={breadcrumb}
              pin={currentHole.lat != null && currentHole.lng != null ? { lat: currentHole.lat, lng: currentHole.lng } : null}
              pinLockedAt={currentHole.pin_locked_at || null}
              courseCenter={(() => {
                // Priority: course-level centroid → average of any other holes
                // that DO have pins → null (means: use plain dark-green fallback).
                if (course.centroid_lat != null && course.centroid_lng != null) {
                  return { lat: course.centroid_lat, lng: course.centroid_lng };
                }
                const pinned = (course.holes || []).filter(
                  (h) => h.lat != null && h.lng != null,
                );
                if (pinned.length === 0) return null;
                const lat = pinned.reduce((s, h) => s + h.lat, 0) / pinned.length;
                const lng = pinned.reduce((s, h) => s + h.lng, 0) / pinned.length;
                return { lat, lng };
              })()}
              distance={distance}
              distanceSource={distanceSource}
              gpsError={gpsError}
              onExpand={() => setShowHoleView(true)}
              onFixPin={
                course?.stats_unknown
                  ? () => {
                      setPinFixMode(true);
                      setShowHoleView(true);
                    }
                  : null
              }
              vsPar={(() => {
                if (!round?.scores?.length) return null;
                const played = round.scores.reduce((acc, s) => acc + (s.strokes || 0), 0);
                const par = round.scores.reduce((acc, s) => {
                  const h = course.holes.find((x) => x.number === s.hole_number);
                  return acc + (h?.par || 0);
                }, 0);
                return played - par;
              })()}
              walkedFromTee={(() => {
                // Distance walked from where the player opened this hole
                // (first breadcrumb entry) to their current GPS position.
                // Falls back to undefined if breadcrumb isn't ready.
                if (!breadcrumb || breadcrumb.length < 2 || !userPos) return undefined;
                const start = breadcrumb[0];
                if (start.lat == null || start.lng == null) return undefined;
                return haversineYards(start.lat, start.lng, userPos.lat, userPos.lng);
              })()}
              totalHoles={course.holes.length}
              scoreChip={(() => {
                // Live scoring chip in the map's top-right corner.
                // Only renders for Stableford / Match Play — strokeplay
                // rounds show total strokes elsewhere.
                const mode = round?.scoring_mode;
                if (!mode || mode === "strokeplay" || !scoring) return undefined;
                if (mode === "stableford") {
                  const me = (scoring.per_player || []).find(
                    (p) => p.player_index === (isFourball ? activePlayer : 0),
                  ) || (scoring.per_player || [])[0];
                  if (!me) return undefined;
                  return {
                    label: scoring.net ? "Net · Stableford" : "Stableford",
                    value: me.points ?? 0,
                    unit: "pts",
                  };
                }
                if (mode === "match_play") {
                  const s = scoring.status || {};
                  const diff = s.diff || 0;
                  const holesRemaining = s.holes_remaining ?? 0;
                  // Decisive close-out (e.g. "3 & 2") — backend already
                  // formats the winning-margin label; surface it verbatim
                  // so the phone matches what real match-play boards show.
                  if (s.decisive && s.label) {
                    return {
                      label: (diff > 0) ? "Match won · closed out" : "Match lost · closed out",
                      value: s.label,
                    };
                  }
                  if (s.complete) {
                    if (diff === 0) {
                      return { label: "Match halved · finished", value: "AS" };
                    }
                    return {
                      label: diff > 0 ? "You win" : "You lose",
                      value: s.label || (diff > 0 ? `${diff} UP` : `${Math.abs(diff)} DN`),
                    };
                  }
                  // Live in-progress: "2 UP thru 8" is the standard club
                  // scoreboard phrasing. Include the "N to play" hint so
                  // the player knows how many holes are left to catch up
                  // (or defend the lead).
                  const value = diff > 0
                    ? `${diff} UP`
                    : diff < 0
                      ? `${Math.abs(diff)} DN`
                      : "AS";
                  const holesPlayed = s.holes_played ?? 0;
                  const throughLabel = holesPlayed > 0 ? ` · thru ${holesPlayed}` : "";
                  return {
                    label: `Match${throughLabel} · ${holesRemaining} to play`,
                    value,
                  };
                }
                return undefined;
              })()}
            />
          </motion.div>
        </AnimatePresence>

        {/* Crowdsourced pin confirmation — when the player is on / near the
            green or this hole has no pin coords at all, surface a one-tap
            "Confirm pin" CTA + a "pin confirmed by N players" badge. */}
        <PinConfirmStrip
          courseId={course.id}
          holeNumber={currentHole.number}
          userPos={userPos}
          gpsAccuracy={gpsAccuracy}
          distance={distance}
          distanceSource={distanceSource}
        />

        {/* AI hole preview — 1-liner caddie strategy from Gemini, cached
            per hole. Silently absent if generation failed or API is down. */}
        {(holePreview || previewLoading) && (
          <div
            className="rounded-xl bg-gradient-to-br from-[#143D2F] to-[#1a5a3e] text-[#F5EAD0] px-4 py-3 mb-3 shadow-sm"
            data-testid="ai-hole-preview"
          >
            <div className="flex items-start gap-2.5">
              <div className="shrink-0 mt-0.5 text-[#D4AF37] font-heading font-black text-[10px] tracking-[2px]">
                CADDIE
              </div>
              <div className="flex-1 text-sm leading-snug font-medium">
                {holePreview || <span className="opacity-70 italic">Thinking&hellip;</span>}
              </div>
            </div>
          </div>
        )}

        {/* Live scoring strip — Stableford points or match-play status.
            Only renders for non-strokeplay rounds; hidden when API hasn't
            replied yet (e.g. offline). */}
        <ScoringStrip
          round={round}
          scoring={scoring}
          currentHole={currentHole}
          activePlayer={activePlayer}
        />

        {/* Mini live leaderboard — sorts by lowest strokes, crown on the
            leader. Rendered HERE (just above the score-input card) instead
            of at the top of the screen, so swapping player on a fourball
            stays a thumb-reach away while you're entering shots. */}
        {isFourball && (() => {
          const played = round.players.filter((p) => p.scores.length > 0);
          const idle = round.players.filter((p) => p.scores.length === 0);
          const sorted = [
            ...played.slice().sort((a, b) => a.total_strokes - b.total_strokes),
            ...idle,
          ];
          const leaderStrokes = played.length > 0 ? sorted[0].total_strokes : null;
          const active = round.players.find((p) => p.player_index === activePlayer);
          return (
            <div data-testid="fourball-player-bar">
              {/* "Now up" callout — big avatar + name so mates on separate
                  phones (or handing one phone around) instantly see whose
                  turn it is without having to read the tab colours. Sits
                  above the leaderboard bar with a soft pulse animation
                  when the active player changes. */}
              {active && (
                <div
                  className="mb-2 flex items-center gap-3 rounded-2xl px-3 py-2 border-2"
                  style={{
                    background: "var(--accent-sand)",
                    borderColor: "var(--accent-terracotta)",
                    boxShadow: "0 4px 14px -6px rgba(196,84,60,.4)",
                  }}
                  key={`nowup-${activePlayer}`}
                  data-testid="fourball-now-up"
                >
                  <div className="relative">
                    {active.avatar_url ? (
                      <img
                        src={active.avatar_url}
                        alt={active.name}
                        className="w-11 h-11 rounded-full object-cover border-2 border-[var(--primary)]"
                        data-testid="fourball-now-up-avatar"
                      />
                    ) : (
                      <div
                        className="w-11 h-11 rounded-full flex items-center justify-center font-heading font-black text-lg text-white border-2 border-[var(--primary)]"
                        style={{ background: "var(--primary)" }}
                        data-testid="fourball-now-up-avatar"
                      >
                        {(active.name || "?").slice(0, 1).toUpperCase()}
                      </div>
                    )}
                    <span
                      className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-black"
                      style={{ background: "var(--accent-terracotta)", color: "#fff" }}
                      title="It's your turn"
                    >
                      →
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[9px] uppercase tracking-widest font-bold text-[var(--accent-terracotta)] leading-none">
                      Now up
                    </p>
                    <p
                      className="font-heading font-black text-lg text-[var(--primary)] truncate leading-tight mt-0.5"
                      data-testid="fourball-now-up-name"
                    >
                      {active.name || "Player"}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[9px] uppercase tracking-widest text-[var(--text-secondary)] font-bold leading-none">Hole</p>
                    <p className="font-mono-num font-black text-xl text-[var(--primary)] leading-none mt-0.5">
                      {currentHole.number}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between mb-1.5">
                <p className="label-uppercase">Leaderboard · tap to swap player</p>
                <p className="text-[10px] text-[var(--text-secondary)] font-bold uppercase tracking-wider">
                  {round.participant_user_ids?.length > 1 ? "live · 10s" : "tap to switch"}
                </p>
              </div>
              <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-1">
                {sorted.map((p) => {
                  const playerVsPar = p.total_strokes - p.total_par;
                  const active = p.player_index === activePlayer;
                  const isLeader = leaderStrokes !== null && p.scores.length > 0 && p.total_strokes === leaderStrokes;
                  return (
                    <button
                      key={p.player_index}
                      onClick={() => setActivePlayer(p.player_index)}
                      data-testid={`fourball-player-tab-${p.player_index}`}
                      className={`shrink-0 rounded-2xl px-3 py-2 text-left border-2 transition-colors min-w-[120px] relative ${
                        active
                          ? "bg-[var(--primary)] text-white border-[var(--primary)]"
                          : "bg-white text-[var(--primary)] border-[var(--border-default)]"
                      }`}
                    >
                      {isLeader && (
                        <span
                          className="absolute -top-1.5 -right-1.5 text-base"
                          title="Leader"
                          aria-label="Leader"
                          data-testid={`fourball-leader-${p.player_index}`}
                        >
                          👑
                        </span>
                      )}
                      <div className="flex items-center gap-1.5 font-heading font-bold text-xs truncate">
                        {p.avatar_url ? (
                          <img
                            src={p.avatar_url}
                            alt=""
                            className={`rounded-md object-cover shrink-0 transition-all ${active ? "w-7 h-7 ring-2 ring-white/60" : "w-5 h-5"}`}
                            data-testid={`fourball-player-avatar-${p.player_index}`}
                          />
                        ) : (
                          <span
                            className={`rounded-md flex items-center justify-center font-bold uppercase transition-all ${
                              active
                                ? "w-7 h-7 text-xs bg-white/25 ring-2 ring-white/60"
                                : "w-5 h-5 text-[10px] bg-[var(--accent-sand)]"
                            }`}
                          >
                            {(p.name || "?").slice(0, 1).toUpperCase()}
                          </span>
                        )}
                        <span className="truncate">{p.name}</span>
                      </div>
                      <div className={`mt-1 font-mono-num text-sm font-bold ${active ? "opacity-95" : ""}`}>
                        {p.total_strokes || 0}
                        <span className={`text-[10px] ml-1 font-normal ${active ? "opacity-75" : "text-[var(--text-secondary)]"}`}>
                          {p.scores.length > 0
                            ? (playerVsPar === 0 ? "E" : playerVsPar > 0 ? `+${playerVsPar}` : playerVsPar)
                            : "—"}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })()}

        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={currentHole.number}
            custom={direction}
            initial={{ opacity: 0, x: 80 * direction }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -80 * direction }}
            transition={{ type: "spring", stiffness: 320, damping: 30 }}
            className="bg-[var(--primary)] text-white rounded-[2rem] p-6 shadow-xl relative overflow-hidden"
          >
            <div className="absolute -right-12 -top-12 h-48 w-48 rounded-full bg-white/5" />
            <div className="absolute -left-10 -bottom-10 h-32 w-32 rounded-full bg-white/5" />
            <span data-testid="active-hole-number" className="sr-only">{currentHole.number}</span>
            <span data-testid="hole-distance-yards" className="sr-only">{distance === null ? "—" : distance}</span>

            <div className="relative flex items-center justify-between gap-2 mb-3">
              <WeatherPill
                playerLat={userPos?.lat}
                playerLng={userPos?.lng}
                pinLat={currentHole.lat}
                pinLng={currentHole.lng}
                onWeather={setWind}
              />
              <div className="flex items-center gap-1.5">
                <TeeSwitchPill
                  round={round}
                  course={course}
                  activePlayer={activePlayer}
                  onTeeChanged={(updated) => {
                    setRound(updated);
                    // Force a fresh GPS distance read for the new tee fallback
                    setDistance(null);
                    setDistanceSource("");
                  }}
                />
                <GpsSignalPill
                  source={distanceSource}
                  accuracy={gpsAccuracy}
                  lastFixAt={gpsLastFixAt}
                  hasError={!!gpsError}
                />
              </div>
            </div>

            <ClubSuggestionChip
              distance={distance}
              wind={wind}
              elevationAdj={elevationAdj}
              clubYardages={clubYardages}
              dispersion={dispersion}
              onGreen={onGreen}
            />

            {/* Strokes stepper */}
            <div className="relative bg-white/10 rounded-2xl p-4 mt-4 backdrop-blur">
              <p className="text-xs uppercase tracking-[0.16em] opacity-80 text-center mb-2">Total shots this hole</p>
              <div className="flex items-center justify-center gap-5">
                <button
                  onClick={() => updateStrokes(-1)}
                  disabled={!strokes || strokes <= 1}
                  className="stepper-btn !bg-white/20 !text-white hover:!bg-white/30"
                  data-testid="stroke-decrement"
                >
                  <MinusCircle size={28} weight="bold" />
                </button>
                <div className="w-28 text-center">
                  <AnimatedNumber
                    value={strokes ?? "—"}
                    className="font-mono-num font-black text-7xl leading-none block"
                    testid="stroke-count"
                  />
                </div>
                <button
                  onClick={() => addStrokeDebounced("manual")}
                  className="stepper-btn !bg-white !text-[var(--primary)] hover:!bg-white/90"
                  data-testid="stroke-increment"
                >
                  <PlusCircle size={28} weight="fill" />
                </button>
              </div>
              <p className="text-center text-xs mt-3 opacity-80" data-testid="stroke-label">
                {strokes ? scoreLabel(strokes, currentHole.par) : "Tap + for every shot — putts included"}
              </p>
              {/* Live breakdown so it's obvious putts are already counted in the total */}
              {strokes ? (
                <p className="text-center text-[11px] mt-1 font-mono-num opacity-90" data-testid="stroke-breakdown">
                  {(() => {
                    const p = putts ?? 0;
                    const toGreen = Math.max(0, strokes - p);
                    if (p === 0) return `${strokes} shot${strokes === 1 ? "" : "s"} to the green · tap a putt button below when you finish`;
                    return `${toGreen} to green + ${p} putt${p === 1 ? "" : "s"} = ${strokes} total`;
                  })()}
                </p>
              ) : (
                <p className="text-center text-[10px] opacity-60 italic mt-1">
                  Tap + for each shot off the green. When you sink it, hit the putt button below.
                </p>
              )}
              {isFourball && !onGreen && (
                <p className="text-center text-[10px] font-bold mt-1 text-[var(--accent-forest)]" data-testid="fourball-turn-hint">
                  Fourball · each + hands the phone to the next player
                </p>
              )}

              {/* GPS shot tracker — logs the current position + club when the
                  user taps "Log shot". Uses the round's live userPos and
                  updates the local `round.shots` array via onShotsChanged. */}
              {round?.id && currentHole?.number && (
                <ShotTracker
                  roundId={round.id}
                  holeNumber={currentHole.number}
                  playerIndex={activePlayer}
                  userPos={userPos}
                  shots={round.shots || []}
                  onShotsChanged={(shots) => setRound((prev) => (prev ? { ...prev, shots } : prev))}
                />
              )}

              {/* AI Swing Coach chip — Pro-only. Opens a bottom sheet so
                  mates can record a swing right on the course without
                  leaving the round screen. The heavy lifting (upload +
                  Gemini analysis + result card) lives in SwingCoach. */}
              <div className="mt-3 pt-3 border-t border-white/15">
                <button
                  type="button"
                  onClick={() => setSwingCoachOpen(true)}
                  data-testid="round-swing-coach-open"
                  className="w-full flex items-center justify-center gap-2 rounded-full bg-white/12 hover:bg-white/20 py-2 text-sm font-bold transition-colors"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/></svg>
                  Analyse a swing
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/20">Pro</span>
                </button>
                <p className="text-center text-[10px] font-bold mt-1 opacity-60">
                  Record your mate's swing → instant AI pro breakdown
                </p>
              </div>

              {/* "On the green" toggle — always visible so auto-detect can fire even before first stroke */}
              <div className="mt-4 pt-3 border-t border-white/15">
                  <button
                    onClick={() => {
                      // If user manually turns OFF, suppress auto-re-enable for 30s
                      if (onGreen) {
                        manualGreenOverrideRef.current = Date.now();
                      }
                      setOnGreen((v) => !v);
                    }}
                    data-testid="toggle-on-green"
                    className={`w-full rounded-2xl py-3 px-4 transition-colors flex items-center justify-between ${
                      onGreen
                        ? "bg-[var(--accent-turf)] text-[var(--primary)]"
                        : "bg-white/10 text-white"
                    }`}
                  >
                    <span className="flex items-center gap-2 font-heading font-bold">
                      <Flag size={18} weight={onGreen ? "fill" : "regular"} />
                      {onGreen ? "On the green · next +1 = putt" : "I'm on the green"}
                    </span>
                    <span className="flex items-center gap-1.5">
                      {autoOnGreenEnabled && (
                        <span
                          className="text-[9px] uppercase font-black tracking-wider px-1.5 py-0.5 rounded"
                          style={{ background: onGreen ? "rgba(20,61,47,0.18)" : "rgba(34,197,94,0.4)", color: onGreen ? "var(--primary)" : "#fff" }}
                          title="Auto-detect ON — triggers when GPS is within 12 yds of pin"
                        >
                          AUTO
                        </span>
                      )}
                      <span className={`text-xs uppercase font-bold tracking-wider ${onGreen ? "text-[var(--primary)]/70" : "opacity-70"}`}>
                        {onGreen ? "ON" : "OFF"}
                      </span>
                    </span>
                  </button>
                  <div className="flex items-center justify-between mt-2 gap-2">
                    {!onGreen && distance !== null && distance <= 15 && distanceSource === "gps" && (
                      <div className="text-[11px] text-[var(--accent-turf)] flex items-center gap-1 flex-1" data-testid="near-pin-hint">
                        <Crosshair size={12} weight="bold" /> Near the pin ({distance} yds)
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => setAutoOnGreenEnabled((v) => !v)}
                      data-testid="toggle-auto-green"
                      className="text-[10px] uppercase tracking-wider font-bold opacity-60 hover:opacity-100 transition-opacity ml-auto"
                    >
                      Auto-detect: {autoOnGreenEnabled ? "ON" : "OFF"} (tap to {autoOnGreenEnabled ? "disable" : "enable"})
                    </button>
                  </div>
                </div>

              {/* Quick putt-then-advance buttons — one tap finishes the hole.
                  Each adds N strokes (the putts), sets putts=N, advances.
                  Only show once the user has logged at least one stroke. */}
              {strokes ? (
                <div className="mt-3 pt-3 border-t border-white/15">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className="text-xs uppercase tracking-[0.16em] opacity-80 block">Finish hole</span>
                      <span className="text-[10px] opacity-60">Tap how many putts you took — we&apos;ll add them to your score; review then tap the green button to advance</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {[1, 2, 3, 4].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => {
                          // Overwrite-safe: peel any previously-logged putts
                          // off the stroke count BEFORE adding the new putt
                          // total, so re-tapping doesn't double-count.
                          const oldPutts = putts ?? 0;
                          const teeToGreen = Math.max(0, (strokes ?? 0) - oldPutts);
                          const nextStrokes = Math.min(15, teeToGreen + n);
                          saveScore({ strokes: nextStrokes, putts: n });
                          // Every player transition is manual in fourball
                          // now — the "Finish hole · Pass to X →" CTA is
                          // what hands over. Solo rounds stay put too.
                        }}
                        data-testid={`putt-finish-${n}`}
                        className="rounded-2xl bg-white text-[var(--primary)] font-heading font-black text-lg py-3 hover:bg-white/90 active:scale-95 transition-transform flex flex-col items-center justify-center gap-0.5 shadow-sm"
                      >
                        <span>{n}</span>
                        <span className="text-[9px] uppercase tracking-wider font-bold opacity-70 normal-case">
                          putt{n === 1 ? "" : "s"}
                        </span>
                      </button>
                    ))}
                  </div>
                  {/* Second row — high putt count + give-up button. Less common
                      so they sit underneath the primary 1-4 row. */}
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <button
                      type="button"
                      onClick={() => {
                        // 5+ putts: assume exactly 5 (rare beyond that); user
                        // can tap the on-summary editor for higher counts.
                        // Overwrite-safe — see the 1-4 buttons above.
                        const oldPutts = putts ?? 0;
                        const teeToGreen = Math.max(0, (strokes ?? 0) - oldPutts);
                        const nextStrokes = Math.min(15, teeToGreen + 5);
                        saveScore({ strokes: nextStrokes, putts: 5 });
                        // No auto-advance — manual CTA tap only.
                      }}
                      data-testid="putt-finish-5plus"
                      className="rounded-2xl bg-white/90 text-[var(--primary)] font-heading font-black text-lg py-3 hover:bg-white active:scale-95 transition-transform flex flex-col items-center justify-center gap-0.5 shadow-sm"
                    >
                      <span>5+</span>
                      <span className="text-[9px] uppercase tracking-wider font-bold opacity-70 normal-case">
                        putts
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        // Pick up — caps the hole at WHS "net double bogey":
                        // par + 2 + strokes_received on this hole. Uses the
                        // player's Course Handicap (Handicap Index adjusted
                        // by the course's rating/slope) when available for
                        // proper WHS. Without rating/slope, falls back to
                        // raw handicap; without stroke_index, falls back to
                        // gross par + 2.
                        const par = currentHole?.par ?? 4;
                        const ch = courseHandicap(
                          user?.handicap,
                          course?.course_rating,
                          course?.slope_rating,
                          course?.par_total,
                        );
                        const received = whsStrokesReceived(ch, currentHole?.stroke_index);
                        const pickUpScore = par + 2 + received;
                        const label = received > 0
                          ? `Pick up — record this hole as ${pickUpScore}?\n(par ${par} + 2 + ${received} handicap stroke${received === 1 ? "" : "s"} = net double bogey)`
                          : `Pick up — record this hole as ${pickUpScore} (net double bogey on par ${par})?`;
                        if (!window.confirm(label)) return;
                        saveScore({ strokes: pickUpScore, putts: 0 });
                        // No auto-advance — manual CTA tap only.
                      }}
                      data-testid="putt-finish-pickup"
                      className="rounded-2xl bg-[var(--accent-terracotta)] text-white font-heading font-black text-base py-3 hover:bg-[var(--accent-terracotta)]/90 active:scale-95 transition-transform flex flex-col items-center justify-center gap-0.5 shadow-sm"
                    >
                      <span>Pick up</span>
                      <span className="text-[9px] uppercase tracking-wider font-bold opacity-80 normal-case">
                        net dbl bogey · {(currentHole?.par ?? 4) + 2 + whsStrokesReceived(courseHandicap(user?.handicap, course?.course_rating, course?.slope_rating, course?.par_total), currentHole?.stroke_index)}
                      </span>
                    </button>
                  </div>
                  {(putts ?? 0) > 0 && (
                    <div className="mt-2 text-[10px] opacity-70 text-center">
                      Already logged {putts} putt{putts === 1 ? "" : "s"} for this hole · tap a number above to overwrite.
                    </div>
                  )}

                  {/* Context-aware "Finish hole" / "Pass to X" / "Finish round"
                      CTA. Only shows after the active player has logged at least
                      a stroke (so we don't surface it on a blank hole). Replaces
                      the previous auto-advance behaviour so the player can
                      review / fix their score before committing. */}
                  {strokes ? (() => {
                    const totalHoles = course?.holes?.length ?? 18;
                    const isLastHole = holeIdx + 1 >= totalHoles;
                    const currentHoleNum = currentHole?.number;
                    let nextPlayerName = null;
                    if (isFourball && round.players && currentHoleNum != null) {
                      const n = round.players.length;
                      for (let step = 1; step <= n; step += 1) {
                        const candidateIdx = (activePlayer + step) % n;
                        if (candidateIdx === activePlayer) break;
                        const ref = localScoreRef.current[localKey(candidateIdx, currentHoleNum)];
                        const persisted = (round.players[candidateIdx]?.scores || [])
                          .some((s) => s.hole_number === currentHoleNum && s.strokes != null);
                        if (!(ref?.strokes != null) && !persisted) {
                          nextPlayerName = round.players[candidateIdx]?.name?.split(" ")[0] || null;
                          break;
                        }
                      }
                    }
                    const finishingRound = isLastHole && !nextPlayerName;
                    // "All fourball players have logged putts on THIS hole"
                    // — this is what the user asked for. When true and
                    // it's not the final hole, the CTA becomes an explicit
                    // "Next hole →" so the user reviews the scorecard
                    // before advancing (no more silent auto-jump).
                    const allFourballFinished = isFourball
                      && round.players
                      && currentHoleNum != null
                      && !nextPlayerName
                      && round.players.every((pl) => {
                        const ref = localScoreRef.current[localKey(pl.player_index, currentHoleNum)];
                        if (ref?.putts != null) return true;
                        return (pl.scores || []).some(
                          (s) => s.hole_number === currentHoleNum && s.putts != null,
                        );
                      });
                    const label = finishingRound
                      ? "Finish round →"
                      : nextPlayerName
                        ? `Finish hole · Pass to ${nextPlayerName} →`
                        : allFourballFinished
                          ? `Next hole (${currentHoleNum + 1}) →`
                          : "Finish hole · Next →";
                    const handleClick = () => {
                      if (finishingRound) {
                        setShowFinish(true);
                      } else if (allFourballFinished && !isLastHole) {
                        // All mates finished this hole — explicit advance
                        // so the user can review before jumping.
                        goToHole(holeIdx + 1);
                      } else {
                        advanceAfterScore();
                      }
                    };
                    return (
                      <>
                        {finishingRound && (
                          <div
                            data-testid="round-complete-toast"
                            className="mt-3 w-full rounded-xl px-3 py-2 flex items-center gap-2 text-xs font-bold"
                            style={{
                              background: "linear-gradient(95deg, #143D2F 0%, #1a5a3e 100%)",
                              color: "#F5EAD0",
                              boxShadow: "0 4px 16px -4px rgba(20,61,47,0.4)",
                            }}
                          >
                            <span className="text-base">🏁</span>
                            <span>
                              {isFourball
                                ? "Everyone's done — tap Finish to see the summary."
                                : "Last hole in the bag — tap Finish to save your round."}
                            </span>
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={handleClick}
                          data-testid={finishingRound ? "finish-round-cta" : "finish-hole-cta"}
                          className="mt-3 w-full rounded-2xl py-3.5 px-4 font-heading font-black text-base tracking-tight transition-transform active:scale-[0.98] shadow-md"
                          style={{
                            background: finishingRound ? "var(--accent-terracotta)" : "var(--accent-turf)",
                            color: finishingRound ? "#F2EEE7" : "var(--primary)",
                          }}
                        >
                          {label}
                        </button>
                      </>
                    );
                  })() : null}
                </div>
              ) : null}
            </div>
          </motion.div>
        </AnimatePresence>

        {error && <div className="text-sm text-[var(--status-bad)] bg-red-50 border border-red-200 rounded-xl px-4 py-3 mt-3">{error}</div>}

        {/* Auto-detected transition to next hole */}
        <AnimatePresence>
          {transitionDetected && nextHole && (
            <motion.button
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              onClick={() => goToHole(holeIdx + 1)}
              data-testid="next-hole-suggestion"
              className="w-full mt-3 rounded-2xl bg-[var(--accent-terracotta)] text-white px-4 py-3 flex items-center justify-between shadow-md"
            >
              <div className="text-left">
                <div className="font-heading font-bold flex items-center gap-1">
                  <Crosshair size={14} weight="bold" /> Looks like you're at Hole {nextHole.number}
                </div>
                <div className="text-[11px] opacity-90">Tap to advance · we'll lock in this hole's score</div>
              </div>
              <CaretRight size={20} weight="bold" />
            </motion.button>
          )}
        </AnimatePresence>

        {/* Quick-log: swing detector + tap-to-tally */}
        <div className="surface-card-flat p-3 mt-4" data-testid="quicklog-panel">
          <p className="label-uppercase mb-2">Quick log</p>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={onArmSwing}
              disabled={!swing.supported || swing.permission === "denied"}
              data-testid="toggle-auto-swing"
              className={`rounded-2xl px-3 py-3 text-left border-2 transition-colors ${
                autoSwing
                  ? "bg-[var(--primary)] text-white border-[var(--primary)]"
                  : "bg-white text-[var(--primary)] border-[var(--border-default)]"
              } ${(!swing.supported || swing.permission === "denied") ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <div className="flex items-center gap-2 font-heading font-bold">
                <Lightning size={18} weight={autoSwing ? "fill" : "regular"} />
                Auto-swing {autoSwing ? "ON" : "OFF"}
              </div>
              <div className={`text-[11px] mt-1 ${autoSwing ? "text-white/80" : "text-[var(--text-secondary)]"}`}>
                {!swing.supported
                  ? "Not supported on this device"
                  : swing.permission === "denied"
                  ? "Motion permission denied"
                  : autoSwing
                  ? "Phone clipped to bag — swings = +1"
                  : "Tap +/− for shots (recommended)"}
              </div>
            </button>

            <button
              onClick={() => setTapMode((v) => !v)}
              data-testid="toggle-tap-mode"
              className={`rounded-2xl px-3 py-3 text-left border-2 transition-colors ${
                tapMode
                  ? "bg-[var(--accent-terracotta)] text-white border-[var(--accent-terracotta)]"
                  : "bg-white text-[var(--primary)] border-[var(--border-default)]"
              }`}
            >
              <div className="flex items-center gap-2 font-heading font-bold">
                <Hand size={18} weight={tapMode ? "fill" : "regular"} />
                Tap-tally {tapMode ? "ON" : "OFF"}
              </div>
              <div className={`text-[11px] mt-1 ${tapMode ? "text-white/85" : "text-[var(--text-secondary)]"}`}>
                {tapMode ? "Big button below — tap each shot" : "Giant button instead of stepper"}
              </div>
            </button>
          </div>

          {autoSwing && (
            <div className="mt-3 flex items-center gap-2 text-xs text-[var(--text-secondary)]" data-testid="auto-swing-armed-status">
              <span className="relative flex h-2 w-2">
                <span className="absolute inset-0 rounded-full bg-[var(--accent-turf)] pulse-dot" />
              </span>
              Armed · live g: <span className="font-mono-num font-bold ml-1">{swing.liveMagnitude.toFixed(1)}</span>
              {lastTrigger?.type === "swing" && Date.now() - lastTrigger.at < 4000 && (
                <span className="ml-auto font-mono-num font-bold text-[var(--status-good)]">Swing detected!</span>
              )}
            </div>
          )}

          {!swing.supported && (
            <div className="mt-2 text-[11px] flex items-center gap-1 text-[var(--text-secondary)]">
              <ShieldWarning size={12} weight="fill" /> Try opening Fairway on a phone for swing detection.
            </div>
          )}
        </div>

        {/* Big tap-tally button */}
        {tapMode && (
          <motion.button
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={() => addStrokeDebounced("tap")}
            data-testid="tap-tally-button"
            className="w-full mt-3 relative overflow-hidden rounded-3xl bg-[var(--accent-terracotta)] text-white px-6 py-10 shadow-xl active:scale-[0.98] transition-transform"
          >
            <div className="font-heading font-black text-3xl tracking-tighter-2">+1 Stroke</div>
            <div className="text-sm opacity-90 mt-1">Tap anywhere here after each shot</div>
            {lastTrigger?.type === "tap" && Date.now() - lastTrigger.at < 800 && (
              <motion.div
                initial={{ scale: 0.6, opacity: 0.8 }}
                animate={{ scale: 1.6, opacity: 0 }}
                transition={{ duration: 0.6 }}
                className="absolute inset-0 rounded-3xl bg-white/30"
              />
            )}
          </motion.button>
        )}

        {/* Hole pager */}
        <div className="flex items-center justify-between mt-5 mb-3">
          <button
            onClick={() => goToHole(holeIdx - 1)}
            disabled={holeIdx === 0}
            className="btn-secondary !py-3 !px-5"
            data-testid="prev-hole-button"
          >
            <CaretLeft size={18} weight="bold" /> Prev
          </button>
          <span className="font-heading font-bold text-[var(--primary)]">{holeIdx + 1} / {course.holes.length}</span>
          {holeIdx < course.holes.length - 1 ? (
            <button onClick={() => goToHole(holeIdx + 1)} className="btn-primary !py-3 !px-5" data-testid="next-hole-button">
              Next <CaretRight size={18} weight="bold" />
            </button>
          ) : (
            <button onClick={() => setShowFinish(true)} className="btn-primary !py-3 !px-5" data-testid="last-hole-finish-button">
              Finish <Check size={18} weight="bold" />
            </button>
          )}
        </div>

        {/* Hole grid */}
        <div className="mb-5 surface-card-flat p-3">
          <p className="label-uppercase mb-2">Hole map{isFourball ? ` · ${activePlayerObj.name}` : ""}</p>
          <div className="grid grid-cols-6 gap-2">
            {course.holes.map((h, i) => {
              const s = activeScores.find((x) => x.hole_number === h.number);
              const active = i === holeIdx;
              return (
                <button
                  key={h.number}
                  onClick={() => goToHole(i)}
                  data-testid={`hole-pill-${h.number}`}
                  className={`rounded-lg py-2 text-xs font-mono-num font-bold relative transition-colors ${
                    active
                      ? "bg-[var(--primary)] text-white"
                      : s
                      ? "bg-[var(--accent-sand)] text-[var(--primary)]"
                      : "bg-white border border-[var(--border-default)] text-[var(--text-secondary)]"
                  }`}
                >
                  {h.number}
                  {s && <span className="block text-[10px] mt-0.5">{s.strokes}</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Totals */}
        <div className="grid grid-cols-3 gap-3 mb-3">
          <Tile label="Holes" value={`${holesPlayed}/${course.holes.length}`} testid="totals-holes" />
          <Tile label="Strokes" value={totalScore} testid="totals-strokes" />
          <Tile label="vs Par" value={vsPar === 0 ? "E" : (vsPar > 0 ? `+${vsPar}` : vsPar)} testid="totals-vs-par" />
        </div>
      </div>

      <AnimatePresence>
        {showInvite && round.join_code && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4"
            onClick={() => setShowInvite(false)}
            data-testid="round-invite-modal"
          >
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ type: "spring", damping: 22, stiffness: 260 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-sm surface-card p-5"
            >
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[3px] font-bold text-[var(--text-secondary)]">Invite mates</div>
                  <h3 className="font-heading font-bold text-xl text-[var(--primary)]">Score together</h3>
                </div>
                <button
                  onClick={() => setShowInvite(false)}
                  aria-label="Close"
                  className="h-9 w-9 rounded-full flex items-center justify-center"
                  style={{ background: "var(--bg-app)" }}
                >
                  <X size={18} weight="bold" />
                </button>
              </div>
              {isPro ? (
                <>
              <p className="text-sm text-[var(--text-secondary)] mb-4">
                Up to 4 players. Mates type the code or tap the link — each scores themselves on their own phone, everyone&apos;s totals sync live.
              </p>
              <div
                className="rounded-2xl p-4 mb-3 text-center"
                style={{ background: "var(--primary)", color: "#F2EEE7" }}
              >
                <div className="text-[10px] uppercase tracking-[3px] opacity-70 font-bold mb-1">Code</div>
                <div className="font-mono-num font-black text-4xl tracking-[6px]" data-testid="round-join-code">{round.join_code}</div>
              </div>
              <button
                onClick={shareInvite}
                className="btn-primary w-full"
                data-testid="round-invite-share-btn"
              >
                <UsersThree size={18} weight="fill" /> Share link
              </button>
              <button
                onClick={async () => {
                  const link = `${window.location.origin}/join-round/${round.join_code}`;
                  try { await navigator.clipboard.writeText(link); } catch {}
                }}
                className="btn-secondary w-full mt-2"
                data-testid="round-invite-copy-link"
              >
                <Copy size={16} weight="bold" /> Copy link
              </button>
              {(round.participant_user_ids?.length || 0) > 1 && (
                <p className="text-[11px] text-center text-[var(--text-secondary)] mt-3">
                  {round.participant_user_ids.length} players joined · scores refresh every 10s
                </p>
              )}
                </>
              ) : (
                <div data-testid="round-invite-pro-gate">
                  <p className="text-sm text-[var(--text-secondary)] mb-4">
                    Fourball sharing is a Pro feature. Go Pro to invite up to 3 mates — each scores on their own phone with everyone&apos;s totals syncing live.
                  </p>
                  <div
                    className="rounded-2xl p-4 mb-4"
                    style={{ background: "var(--accent-sand)" }}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkle size={16} weight="fill" className="text-[var(--accent-terracotta)]" />
                      <div className="font-heading font-bold text-sm text-[var(--primary)]">Pro unlocks</div>
                    </div>
                    <ul className="space-y-1 text-xs text-[var(--text)]">
                      <li className="flex items-start gap-2">
                        <Check size={12} weight="bold" className="text-[var(--primary)] shrink-0 mt-0.5" /> Live multi-device fourball
                      </li>
                      <li className="flex items-start gap-2">
                        <Check size={12} weight="bold" className="text-[var(--primary)] shrink-0 mt-0.5" /> One-tap WhatsApp / iMessage share link
                      </li>
                      <li className="flex items-start gap-2">
                        <Check size={12} weight="bold" className="text-[var(--primary)] shrink-0 mt-0.5" /> Branded shareable fourball recap at the end
                      </li>
                    </ul>
                  </div>
                  <Link
                    to="/subscribe"
                    onClick={() => setShowInvite(false)}
                    className="btn-primary w-full"
                    data-testid="round-invite-go-pro"
                  >
                    <Lock size={16} weight="bold" /> Go Pro to unlock
                  </Link>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showFinish && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 60, opacity: 0 }}
              transition={{ type: "spring", stiffness: 320, damping: 30 }}
              className="bg-white rounded-3xl w-full max-w-sm p-6"
              data-testid="finish-dialog"
            >
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-heading font-bold text-xl text-[var(--primary)]">Finish round?</h3>
                <button onClick={() => setShowFinish(false)}><X size={20} /></button>
              </div>
              <p className="text-[var(--text-secondary)] mb-4">
                You've played <span className="font-bold">{holesPlayed}/{course.holes.length}</span> holes.
                This will lock the round and upload to your league if applicable.
              </p>
              <div className="flex gap-2">
                <button onClick={() => setShowFinish(false)} className="btn-secondary flex-1" data-testid="cancel-finish">Keep playing</button>
                <button onClick={completeRound} className="btn-primary flex-1" data-testid="confirm-finish">Finish</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Watch link modal */}
      <AnimatePresence>
        {showWatchLink && watchLink && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowWatchLink(false)}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(20,61,47,0.6)" }}
            data-testid="watch-link-modal"
          >
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 30, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="surface-card p-5 max-w-sm w-full"
            >
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-heading font-bold text-xl text-[var(--primary)]">Watch link</h3>
                <button onClick={() => setShowWatchLink(false)}><X size={20} /></button>
              </div>
              <p className="text-sm text-[var(--text-secondary)] mb-3">
                Open this URL on your smartwatch browser (Galaxy Watch / Wear OS).
                Big-button play: <strong>Shot</strong>, <strong>Putt</strong>, <strong>Next</strong>. Syncs back to your app.
              </p>
              <div
                className="rounded-xl px-3 py-2 mb-3 text-xs font-mono-num break-all"
                style={{ background: "var(--accent-sand)", color: "var(--primary)" }}
                data-testid="watch-link-url"
              >
                {watchLink.url}
              </div>
              <div className="flex gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard?.writeText(watchLink.url);
                    setWatchCopied(true);
                    setTimeout(() => setWatchCopied(false), 2000);
                  }}
                  data-testid="watch-link-copy"
                  className="btn-primary flex-1"
                >
                  {watchCopied ? "Copied ✓" : "Copy link"}
                </button>
                {navigator.share && (
                  <button
                    type="button"
                    onClick={() => navigator.share({ url: watchLink.url, title: "Fairway watch link" }).catch(() => {})}
                    data-testid="watch-link-share"
                    className="btn-secondary flex-1"
                  >
                    Share…
                  </button>
                )}
              </div>
              <p className="text-[10px] text-[var(--text-secondary)] leading-relaxed">
                The link only works for this round. Anyone with the link can log shots on your behalf, so keep it private (email it to yourself or paste in your watch browser).
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Auto-on-green toast */}
      <AnimatePresence>
        {autoGreenToast && (
          <motion.div
            initial={{ y: -40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -40, opacity: 0 }}
            transition={{ type: "spring", damping: 22 }}
            className="fixed top-3 left-1/2 -translate-x-1/2 z-[60] px-4 py-2.5 rounded-full shadow-xl flex items-center gap-2 pointer-events-none"
            style={{ background: "var(--accent-turf, #C7E0C6)", color: "var(--primary)" }}
            data-testid="auto-on-green-toast"
          >
            <Flag size={16} weight="fill" />
            <span className="text-sm font-heading font-bold">On the green — next +1 = putt</span>
          </motion.div>
        )}
      </AnimatePresence>

      <ShotClubPicker
        open={!!pendingShot}
        carry={pendingShot?.carry}
        holeNumber={pendingShot?.holeNumber}
        roundId={round?.id}
        onClose={() => setPendingShot(null)}
        onSaved={refreshDispersion}
      />

      {showHoleView && currentHole && (
        <HoleView
          playerPos={userPos}
          breadcrumb={breadcrumb}
          pin={{ lat: currentHole.lat, lng: currentHole.lng }}
          holeNumber={currentHole.number}
          par={currentHole.par}
          cardYards={currentHole.yards}
          onClose={() => {
            setShowHoleView(false);
            setPinFixMode(false);
          }}
          onSubmitPin={
            pinFixMode
              ? async ({ lat, lng }) => {
                  const { data } = await api.post(
                    `/courses/${course.id}/submit-pin`,
                    { hole_number: currentHole.number, lat, lng }
                  );
                  // Refresh course coords if auto-approved
                  if (data?.auto_approved) {
                    try {
                      const { data: c } = await api.get(`/courses/${course.id}`);
                      setCourse(c);
                    } catch (e) {
                      console.warn("[Round] post-action course refresh failed", e);
                    }
                  }
                  return data;
                }
              : null
          }
        />
      )}

      {/* AI Swing Coach — bottom-sheet modal launched from the "Analyse a
          swing" chip. Renders SwingCoach in a self-contained sheet so the
          round screen behind stays intact. */}
      {swingCoachOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/55"
          onClick={() => setSwingCoachOpen(false)}
          data-testid="round-swing-coach-sheet"
        >
          <div
            className="w-full max-w-md rounded-t-3xl bg-[var(--bg-card)] max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <SwingCoach onClose={() => setSwingCoachOpen(false)} />
          </div>
        </div>
      )}
    </div>
  );
}
